import { useState } from 'react'
import { BOARD_CODES } from '../../pages/supervisor/boardCodes'

export type ScheduleDefaultFormValues = {
  weekday: string
  category: 'work' | 'off'
  assignment?: string
}

// A default is a normal week: a workday, with or without an assignment, or a
// regular day off. The other codes (leave, training, a detail, unavailable)
// describe a particular day, not what someone normally works.
const DEFAULT_CODES = BOARD_CODES.filter((code) => code.category === 'work' || code.category === 'off')

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

/**
 * docs/product/scheduling-model.md's "Schedule defaults": one of the two
 * ways a supervisor edits an employee's default, "edit individual
 * weekdays." The status and assignment field is the same type-to-enter
 * autocomplete over boardCodes.ts's own catalog SupervisorPlanningBoardPage.tsx's
 * own cell editor uses, so a weekday's new default reads exactly like a
 * cell the board itself would show, rather than a second, differently
 * shaped code list. There is no start time, end time, or meal period field
 * yet, since this pilot has the one shift template and no meal-period data
 * (SupervisorPersonnelProfilePage.tsx's own doc comment).
 */
export default function ScheduleDefaultForm({
  onSave,
  onCancel,
}: {
  onSave: (values: ScheduleDefaultFormValues) => void
  onCancel: () => void
}) {
  const [weekday, setWeekday] = useState(WEEKDAYS[0])
  const [codeInput, setCodeInput] = useState('')

  const matchedCode = DEFAULT_CODES.find((code) => code.code.toLowerCase() === codeInput.trim().toLowerCase())
  const canSave = matchedCode !== undefined

  function save() {
    if (!matchedCode) return
    // A code's own detail is the assignment a work day carries
    // (SupervisorPlanningBoardPage.tsx's applyCode reads the same field the
    // same way); a plain scheduled workday or a day off has none.
    onSave({ weekday, category: matchedCode.category as 'work' | 'off', assignment: matchedCode.detail })
  }

  return (
    <div className="flex flex-col gap-3 rounded-surface border border-line bg-canvas p-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="schedule-default-weekday" className="mb-1 block text-caption font-bold text-ink-muted">
            Weekday
          </label>
          <select
            id="schedule-default-weekday"
            value={weekday}
            onChange={(event) => setWeekday(event.target.value)}
            className="min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small"
          >
            {WEEKDAYS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="schedule-default-code" className="mb-1 block text-caption font-bold text-ink-muted">
            Status or assignment code
          </label>
          <input
            id="schedule-default-code"
            type="text"
            list="schedule-default-codes"
            value={codeInput}
            onChange={(event) => setCodeInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                save()
              }
            }}
            placeholder="e.g. Case Processing"
            className="min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small"
          />
          <datalist id="schedule-default-codes">
            {DEFAULT_CODES.map((code) => (
              <option key={code.code} value={code.code} />
            ))}
          </datalist>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={save}
          disabled={!canSave}
          className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-40"
        >
          Save weekday
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex min-h-11 items-center justify-center rounded-control px-4 text-small font-bold text-ink-muted hover:bg-surface-2"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
